/** @jsxImportSource @emotion/react */
import { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { SubTitleLayout, SubTitleGroup } from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import _ from 'lodash';
import CustomGrid from 'components/grids/CustomGrid';
import { MdDtlIspt, MdDtlIsptCondition } from 'models/md/MdIves';
import { findMdIvesDtlIsptPopupList } from 'apis/md/MdProject';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
} from 'components/layouts/SearchBox';
import { MultiComboBox } from 'components/selects/ComboBox';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';

type Props = {
  open: boolean;
  close: () => void;
  ids: string[];
  callbackFn: (selectedItems: any) => void;
};

/**
 * 상세 점검사항 추가 팝업
 */
const DtlIsptInqPopup = ({ open, close, callbackFn, ...props }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();

  const initCondition = {
    prdnProcCds: [],
    eqclIds: [],
    dtlIsptNm: '',
  };

  const gridRef = useRef<any>();
  const [prdnProcCode, setPrdnProcCode] = useState<Code[]>([]);
  const [eqclCode, setEqclCode] = useState<Code[]>([]);
  const [rowData, setRowData] = useState<MdDtlIspt[]>([]);
  const [condition, setCondition] = useState<MdDtlIsptCondition>(initCondition);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'prdnProcCdNm',
        header: String(t('com.label.공정', '공정')),
        minWidth: 70,
        width: 100,
      },
      {
        binding: 'eqclNm',
        header: String(t('com.label.설비군', '설비군')),
        minWidth: 70,
        width: 120,
      },
      {
        binding: 'dtlIsptNm',
        header: String(t('com.label.비표준 항목', '비표준 항목')),
        minWidth: 70,
        width: 240,
      },
      {
        header: String(t('com.label.수량 기준', '수량 기준')),
        columns: [
          {
            binding: 'qtyUnitCdNm',
            header: String(t('com.label.기준', '기준')),
            minWidth: 70,
            width: '*',
          },
          {
            binding: 'msrScf',
            header: String(t('com.label.배수', '배수')),
            minWidth: 70,
            width: 80,
          },
          {
            binding: 'addQty',
            header: String(t('com.label.추가', '추가')),
            minWidth: 70,
            width: 80,
          },
          {
            binding: 'calLogcCtn',
            header: String(t('com.label.계산로직', '계산로직')),
            minWidth: 70,
            width: '*',
          },
        ],
      },
      {
        binding: 'uprcDcnTpCdNm',
        header: String(t('com.label.Ref 기준', 'Ref 기준')),
        minWidth: 70,
        width: '*',
      },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.formatItem.addHandler((s, e) => {
      if (e.panel === s.rowHeaders && e.col === 0) {
        const item = s.rows[e.row].dataItem;

        if (props.ids.includes(`${item.prdnProcCd}_${item.eqclId}_${item.dtlIsptId}`)) {
          e.cell.removeChild(e.cell.firstChild);
        }
      }
    });
  };

  const initCodes = async () => {
    const PRDN_CODE = await getCommonCodeNamesCondition({
      cmnGrCd: 'PRDN_PROC_CD',
      optValCtn7: 'Y',
    });
    setPrdnProcCode(PRDN_CODE);
    const EQCL_ID = await getCommonCodeNamesCondition({
      cmnGrCd: 'MD_EQCL_ID',
    });
    setEqclCode(EQCL_ID);
  };

  const onBeginningEdit = (s, e) => {
    const item = s.rows[e.row].dataItem;
    if (props.ids.includes(`${item.prdnProcCd}_${item.eqclId}_${item.dtlIsptId}`)) {
      // 숨긴 행과 동일한 조건
      e.cancel = true; // 편집 및 선택 방지
    }
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const btnSearch = () => {
    // search
    openLoading(true);
    findMdIvesDtlIsptPopupList(condition)
      .then((res) => {
        setRowData(res || []);
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.데이터 조회 중 오류가 발생했습니다.',
            '데이터 조회 중 오류가 발생했습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  const btnSave = () => {
    const selectedItems = gridRef.current.rows
      .filter((row) => row.isSelected)
      .map((o) => o.dataItem);
    if (selectedItems.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 데이터가 없습니다.', '선택된 데이터가 없습니다.'),
      });
      return;
    }

    callbackFn(selectedItems);
    close();
  };

  useEffect(() => {
    initCodes();
    btnSearch();
  }, []);

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={btnSave}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];
  return (
    <CustomDialog
      title={String(t('md.label.상세 점검사항 리스트', '상세 점검사항 리스트'))}
      size={'lg'}
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{t('com.label.공정', '공정')}</label>
                <MultiComboBox
                  id="prdnProcCode"
                  options={prdnProcCode}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={condition.prdnProcCds}
                  onChange={(value) => handleOnChange('prdnProcCds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.설비군', '설비군')}</label>
                <MultiComboBox
                  id="eqclCode"
                  options={eqclCode}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={condition.eqclIds}
                  onChange={(value) => handleOnChange('eqclIds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.비표준항목', '비표준항목')}</label>
                <CustomInputWithSearch
                  name="dtlIsptNm"
                  value={condition?.dtlIsptNm}
                  onChange={(e) => handleOnChange('dtlIsptNm', e.target.value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={initCondition}
            setReset={setCondition}
            onSearch={btnSearch}
          />
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.상세 점검사항 목록', '상세 점검사항 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={500}
        isReadOnly={true}
        allowPinning={false}
        align="center"
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
      />
    </CustomDialog>
  );
};

export default DtlIsptInqPopup;
